import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import { RenderFormikInput } from "./../../../../Components/Common/Forms/FormikInputHelper";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Alert,
} from "reactstrap";
import { useLinksStore } from "../../../../stores/Assets/links";
import { useSpaceStore } from "../../../../stores/Assets/space";
import ReactSelect from "react-select";
import { reactSelectStyling } from "../../../../Components/Common/Forms/FormikInputHelper";

const AddLink = ({ sites, showAddLinkModal, toggleAddLinkModal }) => {
  const { createLinkMultiple, checkUserExists, sendEmailInvitation, isLoading } = useLinksStore((state) => state);
  const { getSpaces, spaces } = useSpaceStore((state) => state);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [selectedSpaces, setSelectedSpaces] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [userExists, setUserExists] = useState(null); // null = not checked, true = exists, false = doesn't exist
  const [checkingUser, setCheckingUser] = useState(false);
  const [userNotFoundMessage, setUserNotFoundMessage] = useState("");
  
  const formik = useFormik({
    initialValues: {
      user_email: "",
      site_id: sites[0]?.site_id || "",
    },
    validationSchema: Yup.object({
      user_email: Yup.string()
        .email(t("Invalid email"))
        .required(t("Required")),
      site_id: Yup.string().required(t("Required")),
    }),
    onSubmit: async (values) => {
      if (userExists === false) {
        // User doesn't exist, send invitation
        const invitationData = {
          email: values.user_email,
          site_id: parseInt(values.site_id),
          space_ids: selectedSpaces.map(s => parseInt(s.value)),
        };

        const result = await sendEmailInvitation(invitationData);
        if (result) {
          setSuccessMessage(`Invitation email sent to ${values.user_email}!`);
          setTimeout(() => {
            formik.resetForm();
            setSelectedSpaces([]);
            setSuccessMessage("");
            setUserExists(null);
            setUserNotFoundMessage("");
            toggleAddLinkModal();
          }, 1500);
        }
      } else if (userExists === true) {
        // User exists, create link
        if (selectedSpaces.length === 0) {
          alert(t("Please select at least one space"));
          return;
        }
        
        const json = {
          email: values.user_email,
          space_ids: selectedSpaces.map(s => parseInt(s.value)),
        };

        const result = await createLinkMultiple(json);
        if (result) {
          setSuccessMessage(`Successfully linked user to ${selectedSpaces.length} space(s)!`);
          setTimeout(() => {
            formik.resetForm();
            setSelectedSpaces([]);
            setSuccessMessage("");
            setUserExists(null);
            setUserNotFoundMessage("");
            toggleAddLinkModal();
          }, 1500);
        }
      } else {
        alert(t("Please wait for user verification to complete"));
      }
    },
  });

  // Check if user exists when email changes
  const checkUser = async (email) => {
    if (!email || !email.includes('@')) {
      setUserExists(null);
      setUserNotFoundMessage("");
      return;
    }

    setCheckingUser(true);
    const exists = await checkUserExists(email);
    setUserExists(exists);
    
    if (!exists) {
      setUserNotFoundMessage(`User with email "${email}" not found. You can send an invitation email instead.`);
    } else {
      setUserNotFoundMessage("");
    }
    setCheckingUser(false);
  };

  // Debounced email checking
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formik.values.user_email) {
        checkUser(formik.values.user_email);
      } else {
        setUserExists(null);
        setUserNotFoundMessage("");
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [formik.values.user_email]);

  useEffect(() => {
    const fetchSpaces = async () => {
      setSpacesLoading(true);
      setSelectedSpaces([]); // Reset selected spaces when site changes
      if (formik.values.site_id) {
        await getSpaces(formik.values.site_id, { page: 1, perPage: 500 });
      }
      setSpacesLoading(false);
    };

    fetchSpaces();
  }, [formik.values.site_id, getSpaces]);
  
  // Convert spaces to React Select options
  const spaceOptions = spaces.map((space) => ({
    value: space.space_id,
    label: `${space.space_id} - ${space.name}`,
  }));

  return (
    <Modal isOpen={showAddLinkModal} toggle={toggleAddLinkModal} size="md">
      <ModalHeader toggle={toggleAddLinkModal}>
        <i className="ri-link me-2"></i>
        {t("Link User to Spaces")}
      </ModalHeader>
      <ModalBody>
        {successMessage && (
          <Alert color="success" className="d-flex align-items-center gap-2">
            <i className="ri-checkbox-circle-line fs-5"></i>
            {successMessage}
          </Alert>
        )}

        {userNotFoundMessage && (
          <Alert color="warning" className="d-flex align-items-center gap-2">
            <i className="ri-user-unfollow-line fs-5"></i>
            {userNotFoundMessage}
          </Alert>
        )}

        {checkingUser && (
          <Alert color="info" className="d-flex align-items-center gap-2">
            <Spinner size="sm" />
            Checking if user exists...
          </Alert>
        )}

        {userExists === true && (
          <Alert color="success" className="d-flex align-items-center gap-2">
            <i className="ri-user-check-line fs-5"></i>
            User found! You can link them to spaces.
          </Alert>
        )}
        
        <form
          onSubmit={formik.handleSubmit}
          className="d-flex flex-column flex-wrap"
        >
          <div className="flex-fill mb-2">
            <div className="flex-fill mb-2 w-100">
              {RenderFormikInput(formik, {
                fieldName: "user_email",
                label: t("User Email"),
                fullWidth: true,
              })}
            </div>

            <Label for="site" className="mb-1">
              {t("Site")} <span className="text-danger">*</span>
            </Label>
            <select
              id="site"
              name="site_id"
              onChange={(e) => {
                formik.handleChange(e);
              }}
              value={formik.values.site_id}
              className={`form-control mb-3 ${
                formik.touched.site_id && formik.errors.site_id
                  ? "is-invalid"
                  : ""
              }`}
            >
              <option value="">{t("Select Site")}</option>
              {sites.map((site) => (
                <option key={site.site_id} value={site.site_id}>
                  {site.site_id} - {site.name}
                </option>
              ))}
            </select>
            {formik.touched.site_id && formik.errors.site_id && (
              <div className="invalid-feedback d-block">
                {formik.errors.site_id}
              </div>
            )}

            <Label for="spaces" className="mb-1">
              {t("Spaces")} <span className="text-danger">*</span>
              <small className="text-muted ms-2">(Select one or multiple)</small>
            </Label>
            <ReactSelect
              {...reactSelectStyling}
              id="spaces"
              options={spaceOptions}
              isMulti
              value={selectedSpaces}
              onChange={setSelectedSpaces}
              placeholder={t("Select Spaces...")}
              isLoading={spacesLoading}
              isDisabled={!formik.values.site_id || spacesLoading}
              className="mb-2"
            />
            {selectedSpaces.length > 0 && (
              <small className="text-success d-block mb-2">
                <i className="ri-checkbox-circle-line me-1"></i>
                {selectedSpaces.length} space(s) selected
              </small>
            )}
          </div>

          <div className="d-flex flex-row align-items-center justify-content-center gap-2 mt-2">
            <Button 
              type="submit" 
              color={userExists === false ? "primary" : "success"} 
              disabled={isLoading || selectedSpaces.length === 0 || userExists === null || checkingUser}
              className="d-flex align-items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" />
                  <span>{userExists === false ? t("Sending Invitation...") : t("Linking...")}</span>
                </>
              ) : (
                <>
                  <i className={userExists === false ? "ri-mail-send-line" : "ri-link"}></i>
                  <span>
                    {userExists === false 
                      ? t("Send Email Invitation") 
                      : userExists === true 
                        ? t("Link to Spaces") 
                        : t("Please wait...")
                    }
                  </span>
                </>
              )}
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                formik.resetForm();
                setSelectedSpaces([]);
                setSuccessMessage("");
                setUserExists(null);
                setUserNotFoundMessage("");
                toggleAddLinkModal();
              }}
              disabled={isLoading}
            >
              {t("Cancel")}
            </Button>
          </div>
        </form>
      </ModalBody>
      <ModalFooter />
    </Modal>
  );
};

export default AddLink;
